import { useState, useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';
import { useCompareOutputsCache } from './useCompareOutputsCache';
import { useOrgLlmFallbackEnabled } from './useOrgLlmFallbackEnabled';
import { triggerCompareWithClaude } from '../utils/TriggerCompareWithClaude';
import { mergeCompareCache } from '../utils/compareOutputsCache';

/**
 * Hook providing "Compare with Claude" UAT functionality for RD pages.
 *
 * Manages the full lifecycle of a comparison request: triggering the
 * backend call (one per section), storing the returned Claude outputs in
 * sessionStorage, and providing Gemini / Claude overlay plus a side-by-side
 * layout that puts Claude in the AI-inputs column.
 *
 * Multi-section support: RD pages typically display paragraphs from
 * multiple report sections (e.g. CI has "Communication", "Social
 * Interaction", etc.). The hook accepts an array of section names and
 * calls the backend for each one sequentially, merging all paragraph
 * outputs into a single flat map keyed by paragraph code.
 *
 * The feature is only available in dev/staging AND only for orgs whose
 * organisations/{id}.llmFallbackConfig.enabled is true. The backend also
 * enforces both gates; this frontend check hides the button for every
 * other staging org so testers are not offered a path that will 412.
 *
 * Fallback drafts are cached in sessionStorage for this tab (survive
 * refresh and in-tab navigation) and wiped on sign-out. They are never
 * written to Firestore, so approvals stay on the stored Gemini draft.
 *
 * @param {Object} params
 * @param {string} params.patientId - Current patient ID
 * @param {string} params.reportStage - e.g. "Clinical Interview"
 * @param {string[]} params.reportSections - Array of section titles to compare
 * @param {Array<string>} [params.conditions] - Diagnostic conditions
 * @param {string} [params.orgId] - organisations/{orgId}; falls back to auth claims
 * @param {string} [params.organisationName] - org display name; falls back to claims
 * @returns {Object} Hook state and actions
 */
export function useAnthropicComparison({
  patientId,
  reportStage,
  reportSections,
  conditions = [],
  orgId,
  organisationName,
} = {}) {
  const { claims } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ completed: 0, total: 0 });

  // Tab-scoped persistence so a refresh does not force a re-generate.
  const [cache, setCache] = useCompareOutputsCache(patientId, reportStage);
  const claudeOutputs = cache?.outputs || null;
  const showClaude = Boolean(cache?.showClaude);
  const sideBySide = Boolean(cache?.sideBySide);

  // Gate: non-prod build AND this org's Firestore fallback flag.
  const isAvailable = useOrgLlmFallbackEnabled({
    orgId: orgId || claims?.orgId,
    organisationName: organisationName || claims?.organisation,
  });

  // Stable reference for sections array to avoid unnecessary re-renders
  const sectionsKey = useMemo(
    () => (reportSections || []).join('|'),
    [reportSections]
  );

  /**
   * Trigger Claude generation for all paragraphs across all sections in scope.
   * Calls the backend once per section sequentially, merging all outputs into
   * a single flat dict of { paragraphCode: claudeText }. Partial successes
   * are preserved — if one section fails, previously-fetched outputs remain.
   */
  const triggerComparison = useCallback(async () => {
    if (!patientId || !reportStage || !reportSections?.length) return;

    setIsLoading(true);
    setError(null);
    setProgress({ completed: 0, total: reportSections.length });

    const merged = {};
    let lastError = null;

    for (let i = 0; i < reportSections.length; i++) {
      try {
        const outputs = await triggerCompareWithClaude({
          patientId,
          reportStage,
          reportSection: reportSections[i],
          conditions,
        });

        // Merge this section's outputs into the accumulated map
        if (outputs && typeof outputs === 'object') {
          Object.assign(merged, outputs);
        }
      } catch (err) {
        lastError = err?.message || err?.code || `Failed on section "${reportSections[i]}"`;
        console.error(`[useAnthropicComparison] Section "${reportSections[i]}" failed:`, err);
      }

      setProgress({ completed: i + 1, total: reportSections.length });
    }

    // Store whatever we managed to generate (partial success is still useful)
    if (Object.keys(merged).length > 0) {
      // Keep an existing side-by-side layout if the clinician regenerates;
      // otherwise land on the Claude overlay so they can see the new drafts.
      setCache((prev) => mergeCompareCache(prev, {
        outputs: merged,
        showClaude: prev?.sideBySide ? false : true,
      }));
    }

    if (lastError) {
      setError(lastError);
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, reportStage, sectionsKey, conditions, setCache]);

  /**
   * Switch the document overlay between Gemini and Claude.
   * Leaves side-by-side so the two single-model views stay mutually
   * exclusive with the split layout.
   */
  const toggleModel = useCallback(() => {
    setCache((prev) => mergeCompareCache(prev, {
      showClaude: !prev?.showClaude,
      sideBySide: false,
    }));
  }, [setCache]);

  /**
   * Explicit view: 'gemini' | 'claude' | 'sideBySide'.
   * Gemini stays the editable source of truth. Claude overlay is read-only
   * in the document. Side-by-side keeps Gemini on the left and puts Claude
   * in the AI-inputs column.
   */
  const setViewMode = useCallback((mode) => {
    setCache((prev) => mergeCompareCache(prev, {
      showClaude: mode === 'claude',
      sideBySide: mode === 'sideBySide',
    }));
  }, [setCache]);

  /**
   * Toggle the split layout. Turning it on forces Gemini onto the document
   * so the two columns are actually different models.
   */
  const toggleSideBySide = useCallback(() => {
    setCache((prev) => {
      const next = !prev?.sideBySide;
      return mergeCompareCache(prev, {
        showClaude: next ? false : Boolean(prev?.showClaude),
        sideBySide: next,
      });
    });
  }, [setCache]);

  /**
   * Returns the appropriate paragraph text for display based on toggle state.
   * Side-by-side always shows Gemini in the document (Claude lives in the
   * right-hand panel). Overlay mode shows Claude when that toggle is on.
   *
   * @param {string} code - The paragraph key (e.g. "ci_COM_referral_information")
   * @param {string} geminiOutput - The original output from Firestore
   * @returns {string} The text to display
   */
  const getDisplayedOutput = useCallback((code, geminiOutput) => {
    if (sideBySide) {
      return geminiOutput;
    }
    if (showClaude && claudeOutputs && claudeOutputs[code] !== undefined) {
      return claudeOutputs[code];
    }
    return geminiOutput;
  }, [showClaude, sideBySide, claudeOutputs]);

  return {
    isAvailable,
    isLoading,
    error,
    claudeOutputs,
    hasComparison: Boolean(claudeOutputs && Object.keys(claudeOutputs).length),
    showClaude,
    sideBySide,
    progress,
    triggerComparison,
    toggleModel,
    setViewMode,
    toggleSideBySide,
    getDisplayedOutput,
  };
}
